// use-global-filters.tsx
import { useMemo } from 'react'
import { atom, useAtom } from 'jotai'
import { WritableAtom } from 'jotai'
import { SelectOption } from 'src/services/base-model'
import { UiFilterState, createInitialUiState, convertSingleUiFilterToApi } from './filters-helpers'

/**
 * Hook for managing global filter state using Jotai atoms.
 *
 * @param initialValues - The initial filter values that define the shape of the filters
 * @param apiFiltersAtom - Jotai atom for global API filter state management.
 * @param uiFiltersAtom - Jotai atom for global UI filter state management.
 * @returns Object containing uiFilters, apiFilters, setFilter, and toggleFilterItem
 *
 * @example
 * const myApiFiltersAtom = atom<Partial<MyFilters> | undefined>(undefined)
 * const myUiFiltersAtom = atom<UiFilterState<Partial<MyFilters>> | undefined>(undefined)
 * const { uiFilters, apiFilters, setFilter } = useGlobalFilters(
 *   { search: '', category: [] },
 *   myApiFiltersAtom,
 *   myUiFiltersAtom
 * )
 */
export const useGlobalFilters = <T extends object>(
  initialValues: Partial<T>,
  apiFiltersAtom: WritableAtom<Partial<T> | undefined, [Partial<T>], void>,
  uiFiltersAtom: WritableAtom<
    UiFilterState<Partial<T>> | undefined,
    [UiFilterState<Partial<T>>],
    void
  >,
) => {
  const [uiFilters, setUiFilters] = useAtom(uiFiltersAtom)
  const [apiFiltersState, setApiFiltersState] = useAtom(apiFiltersAtom)

  const initializedUiFilters = uiFilters ?? createInitialUiState(initialValues)

  const setFilter = <K extends keyof T>(filterName: K, value: UiFilterState<Partial<T>>[K]) => {
    const newUiFilters = {
      ...initializedUiFilters,
      [filterName]: value,
    }
    setUiFilters(newUiFilters as UiFilterState<Partial<T>>)

    const apiValue = convertSingleUiFilterToApi(value, (initialValues as Partial<T>)[filterName])
    setApiFiltersState({
      ...apiFiltersState,
      [filterName]: apiValue,
    })
  }

  const toggleFilterItem = (filterName: keyof T, item: SelectOption) => {
    const filterValue = initializedUiFilters[filterName]
    if (!Array.isArray(filterValue)) {
      console.warn(`toggleFilterItem called on a non-array filter: ${String(filterName)}`)
      return
    }
    const currentArray = filterValue as SelectOption[]
    const index = currentArray.findIndex((i) => i.value === item.value)
    const newArray =
      index === -1 ? [...currentArray, item] : currentArray.filter((i) => i.value !== item.value)

    const newUiFilters = { ...initializedUiFilters, [filterName]: newArray }
    setUiFilters(newUiFilters as UiFilterState<Partial<T>>)

    const apiValue = convertSingleUiFilterToApi(
      newArray as any,
      (initialValues as Partial<T>)[filterName],
    )
    setApiFiltersState({
      ...apiFiltersState,
      [filterName]: apiValue,
    })
  }

  /** Put every filter back to its initial value, except the ones listed in `keep`. */
  const resetFilters = (keep: (keyof T)[] = []) => {
    const initial = createInitialUiState(initialValues) as UiFilterState<Partial<T>>
    for (const key of keep) {
      initial[key] = initializedUiFilters[key]
    }
    setUiFilters(initial)
    const api: Partial<T> = {}
    for (const key in initialValues) {
      api[key as keyof T] = convertSingleUiFilterToApi(
        initial[key as keyof T],
        (initialValues as Partial<T>)[key as keyof T],
      )
    }
    setApiFiltersState(api)
  }

  // Converts UI state back to what the API expects.
  const apiFilters = useMemo(() => {
    const result: Partial<T> = {}
    for (const key in initialValues) {
      result[key as keyof T] = convertSingleUiFilterToApi(
        initializedUiFilters[key as keyof T],
        (initialValues as Partial<T>)[key as keyof T],
      )
    }
    return result
  }, [initializedUiFilters, initialValues])

  return {
    uiFilters: initializedUiFilters,
    apiFilters,
    setFilter,
    toggleFilterItem,
    resetFilters,
  }
}
